/**
 * useSettlementMutations — Stories 2.5 + 2.6
 *
 * - useUpdateSettlementStatus: PATCH /api/v2/settlements/{id} (Status-Kette
 *   draft → approved → settled) mit OPTIMISTISCHEM Update ueber alle
 *   ['settlements']-Caches (Liste + Detail) und Rollback bei Fehler.
 * - useGenerateSettlements: POST /api/v2/settlements/generate.
 *   CONTRACT-LUECKE (dokumentiert, NICHT erfunden): Der Story-Auftrag nennt
 *   "202 + Status-Polling"; der generierte Welle-2-Contract deklariert fuer
 *   den Lauf aber ausschliesslich eine SYNCHRONE 201-Antwort mit
 *   SettlementGenerateResponse (created/updated/skipped_locked) — es existiert
 *   weder ein 202 noch ein Job-Status-Endpunkt. Umsetzung: 201 wird direkt
 *   ausgewertet; antwortet ein zukuenftiges Backend abweichend mit 202
 *   (leerer Body), faellt der Hook tolerant auf Listen-Polling zurueck
 *   (Refetch bis der Ziel-Monat auftaucht, max. Versuche s.u.). Bleibt der
 *   Monat nach allen Versuchen unsichtbar, liefert der Hook explizit
 *   { kind: 'timeout' } zurueck (FIN-BILLING-SILENT-FAIL) — vorher endete
 *   dieser Fall kommentarlos ohne jede Meldung fuer den Nutzer.
 * - downloadSettlementsCsv / downloadSettlementDocument: Blob-Downloads mit
 *   Auth ueber den api-client (gleiches Muster wie downloadPocArchiveCsv).
 */
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import type { components } from '@heliumtracker/api-types'
import type {
  Settlement,
  SettlementFilters,
  SettlementListResponse,
  SettlementStatus,
} from './useSettlements'
import { previousPeriodMonth } from './settlementUtils'

// --- Status-Umschalter (optimistisch mit Rollback) ---------------------------

type DetailResponse = components['schemas']['SettlementResponse']

function patchSettlement(s: Settlement, status: SettlementStatus): Settlement {
  const now = new Date().toISOString()
  return {
    ...s,
    status,
    // status_label ist ein BE-lokalisiertes Feld — optimistisch NICHT erfunden;
    // die UI rendert Labels ohnehin aus dem eigenen i18n-Namespace.
    ...(status === 'approved' ? { approved_at: s.approved_at ?? now } : {}),
    ...(status === 'settled' ? { settled_at: s.settled_at ?? now } : {}),
  }
}

export function useUpdateSettlementStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: number
      status: SettlementStatus
    }): Promise<Settlement | undefined> => {
      const result = await apiClient.PATCH('/api/v2/settlements/{id}', {
        params: { path: { id } },
        body: { settlement: { status } },
      })
      if (result.error) throw result.error
      return result.data?.data
    },

    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ['settlements'] })

      // Snapshot ALLER Settlement-Caches fuer den Rollback.
      const snapshot = queryClient.getQueriesData({ queryKey: ['settlements'] })

      // Listen optimistisch patchen (Betraege/Totals bleiben unveraendert —
      // die Summen je Status korrigiert erst der Refetch in onSettled).
      queryClient.setQueriesData<SettlementListResponse>(
        { queryKey: ['settlements', 'list'] },
        (old) =>
          old
            ? {
                ...old,
                data: old.data?.map((s) => (s.id === id ? patchSettlement(s, status) : s)),
              }
            : old,
      )

      // Detail (Payout-Sheet) optimistisch patchen.
      queryClient.setQueriesData<DetailResponse>(
        { queryKey: ['settlements', 'detail', id] },
        (old) => (old?.data ? { ...old, data: patchSettlement(old.data, status) } : old),
      )

      return { snapshot }
    },

    onError: (_error, _vars, context) => {
      // Rollback: alle Caches exakt auf den Stand vor dem optimistischen Patch.
      for (const [key, data] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, data)
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['settlements'] })
      // Der Hosts-Screen leitet seinen Auszahlungs-Status aus denselben Daten ab.
      void queryClient.invalidateQueries({ queryKey: ['hosts', 'settlements'] })
    },
  })
}

// --- Monatslauf (Generate) ---------------------------------------------------

export type GenerateMeta = NonNullable<
  components['schemas']['SettlementGenerateResponse']['meta']
>

export type GenerateResult =
  | { kind: 'completed'; meta: GenerateMeta | undefined }
  /**
   * 202-Fallback (Contract-Luecke, siehe Kopfkommentar): Lauf wurde angenommen,
   * aber der Ziel-Monat ist nach POLL_ATTEMPTS Versuchen noch nicht in der
   * Liste aufgetaucht. FIN-BILLING-SILENT-FAIL: vorher wurde dieser Fall
   * kommentarlos verschluckt (die Seite zeigte danach gar nichts mehr an) —
   * jetzt wird er als eigener Ergebnis-Typ durchgereicht, damit die Seite eine
   * Fehlermeldung zeigen kann statt einfach zu verstummen.
   */
  | { kind: 'timeout'; periodMonth: string }

const POLL_ATTEMPTS = 10
const POLL_INTERVAL_MS = 2000

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** true, sobald ein Listen-Cache eine Abrechnung des Ziel-Monats enthaelt. */
function monthVisible(queryClient: QueryClient, periodMonth: string): boolean {
  const lists = queryClient.getQueriesData<SettlementListResponse>({
    queryKey: ['settlements', 'list'],
  })
  return lists.some(([, data]) =>
    (data?.data ?? []).some((s) => s.period_month === periodMonth),
  )
}

async function pollUntilVisible(
  queryClient: QueryClient,
  periodMonth: string,
): Promise<boolean> {
  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
    await delay(POLL_INTERVAL_MS)
    await queryClient.refetchQueries({ queryKey: ['settlements', 'list'] })
    if (monthVisible(queryClient, periodMonth)) return true
  }
  return false
}

export function useGenerateSettlements() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (periodMonth?: string): Promise<GenerateResult> => {
      const result = await apiClient.POST('/api/v2/settlements/generate', {
        body: periodMonth ? { period_month: periodMonth } : {},
      })
      if (result.error) throw result.error
      if (result.response.status === 202) {
        // Async-Variante (im Contract NICHT deklariert) — Ziel-Monat fuer das
        // Polling: expliziter Monat oder Contract-Default "Vormonat". Das
        // Polling laeuft HIER (innerhalb der mutationFn), damit isPending
        // waehrenddessen wahr bleibt und das Ergebnis (gefunden/Timeout) im
        // aufgeloesten GenerateResult landet statt in einem Seiteneffekt, der
        // mit dem bereits aufgeloesten mutateAsync-Promise race'n wuerde.
        const targetMonth = periodMonth ?? previousPeriodMonth(new Date())
        const found = await pollUntilVisible(queryClient, targetMonth)
        return found ? { kind: 'completed', meta: undefined } : { kind: 'timeout', periodMonth: targetMonth }
      }
      return { kind: 'completed', meta: result.data?.meta }
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settlements'] })
      void queryClient.invalidateQueries({ queryKey: ['hosts', 'settlements'] })
    },
  })

  return mutation
}

// --- Downloads (CSV / PDF-Beleg) ---------------------------------------------

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** CSV-Export mit denselben Filtern wie die Liste. Wirft bei HTTP-Fehlern. */
export async function downloadSettlementsCsv(filters: SettlementFilters): Promise<void> {
  const result = await apiClient.GET('/api/v2/settlements/export', {
    params: {
      query: {
        ...(filters.month ? { month: filters.month } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.hostId !== undefined ? { host_id: filters.hostId } : {}),
      },
    },
    parseAs: 'blob',
  })
  if (result.error || !result.data) throw new Error('settlements_export_failed')
  saveBlob(result.data as Blob, `settlements-${filters.month ?? 'all'}.csv`)
}

/** PDF-Beleg einer Abrechnung. Wirft bei HTTP-Fehlern. */
export async function downloadSettlementDocument(
  id: number,
  invoiceNumber?: string,
): Promise<void> {
  const result = await apiClient.GET('/api/v2/settlements/{id}/document', {
    params: { path: { id } },
    parseAs: 'blob',
  })
  if (result.error || !result.data) throw new Error('settlement_document_failed')
  saveBlob(result.data as Blob, `${invoiceNumber ?? `settlement-${id}`}.pdf`)
}
